import {
  Skeleton as ChakraSkeleton,
  SkeletonText as ChakraSkeletonText,
  SkeletonCircle as ChakraSkeletonCircle,
  type Tokens,
} from "@chakra-ui/react-v3";

// Tests find placeholders by `data-cy="skeleton"`; Tiger's v3 recipes set no
// `chakra-*` class to select. Wrapping skeletons stay mounted after loading,
// so only placeholders still on screen carry the marker.
const placeholderDataCy = (loading: boolean | undefined) =>
  loading === false ? undefined : "skeleton";

export type SkeletonProps = {
  height?: Tokens["sizes"];
  width?: Tokens["sizes"];
  /** Match the corner radius of the element it stands in for, e.g. `full` for a pill-shaped tag. Defaults to `sm`. */
  borderRadius?: Tokens["radii"];

  loading?: boolean;

  asChild?: boolean;
  children?: React.ReactNode;
};

/**
 * Shimmering placeholder that reserves the space of incoming content.
 * Prefer it over Loader whenever the final layout is known. Wrap the real
 * content and drive `loading` instead of writing `loading ? <Skeleton/> : x`
 * ternaries; size standalone skeletons with design tokens, not raw px.
 * A standalone Skeleton has no intrinsic size — without `height`/`width` or
 * children it renders zero-height. Use SkeletonText for paragraphs and
 * SkeletonCircle for round shapes.
 */
export function Skeleton(props: SkeletonProps) {
  const { height, width, borderRadius, loading, asChild, children } = props;

  return (
    <ChakraSkeleton
      height={height}
      width={width}
      borderRadius={borderRadius}
      loading={loading}
      asChild={asChild}
      data-cy={placeholderDataCy(loading)}
    >
      {children}
    </ChakraSkeleton>
  );
}

export type SkeletonCircleProps = {
  size?: Tokens["sizes"];

  loading?: boolean;

  children?: React.ReactNode;
};

/**
 * Round placeholder for avatars, status dots, and icon-sized content. Size it
 * with the same token the final element uses so nothing shifts when it loads,
 * and wrap that element to reveal it when `loading` turns false.
 */
export function SkeletonCircle(props: SkeletonCircleProps) {
  const { size = "12", loading, children } = props;

  return (
    <ChakraSkeletonCircle
      size={size}
      loading={loading}
      data-cy={placeholderDataCy(loading)}
    >
      {children}
    </ChakraSkeletonCircle>
  );
}

export type SkeletonTextProps = {
  noOfLines?: number;

  gap?: Tokens["spacing"];
};

/**
 * Multi-line text placeholder. Use it for paragraphs and stacked labels
 * instead of hand-stacking individual Skeleton bars; `noOfLines` matches the
 * expected line count of the final text. It stands in for text that does not
 * exist yet; to reveal text that is already rendered, wrap it in Skeleton.
 */
export function SkeletonText(props: SkeletonTextProps) {
  const { noOfLines = 1, gap } = props;

  return (
    <ChakraSkeletonText
      noOfLines={noOfLines}
      gap={gap}
      data-cy={placeholderDataCy(true)}
    />
  );
}
